import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faSearch, faXmark, faTrophy, faCrown, faLayerGroup, faFire, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { UserItem } from '../types/activity';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDuration } from '../utils/dateUtils';

// Icon-Mapping für Item-Icons
const ITEM_ICONS: Record<string, any> = {
  'fa-bed': faBed,
  'fa-search': faSearch,
  'fa-trophy': faTrophy,
  'fa-crown': faCrown,
  'fa-layer-group': faLayerGroup,
  'fa-fire': faFire,
  'fa-calendar': faCalendar,
};

interface Props {
  items: UserItem[];
  onItemUsed?: () => void;
  disabled?: boolean;
  itemUsedToday?: boolean;
  uiStates?: any;
}

export default function ItemBar({ items, onItemUsed, disabled = false, itemUsedToday = false, uiStates }: Props) {
  const { token } = useAuth();
  const safeItems = Array.isArray(items) ? items : [];
  const [localItems, setLocalItems] = useState<UserItem[]>(safeItems);
  const [activating, setActivating] = useState<number | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [openTooltip, setOpenTooltip] = useState<number | null>(null);
  const [locallyUsedToday, setLocallyUsedToday] = useState(false);

  // Lokale Items mit Props synchronisieren
  useEffect(() => { setLocalItems(items); }, [items]);
  useEffect(() => { setLocallyUsedToday(false); }, [itemUsedToday]);
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const todayStr = new Date().toLocaleDateString('sv-SE');

  // Prüfe ob heute bereits ein Item verwendet wurde
  const usedToday = itemUsedToday || locallyUsedToday
    || localItems.some(i => i.is_used && i.used_on_day === todayStr);

  // Tooltip schließen bei Klick außerhalb (Item + Tooltip = eine Einheit)
  useEffect(() => {
    if (openTooltip === null) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenTooltip(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openTooltip]);

  const handleUseItem = useCallback(async (item: UserItem) => {
    if (disabled) {
      setMessage({ text: 'No active cycle. Items can only be used during an active week.', type: 'error' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (item.is_used) return;
    if (usedToday) {
      setMessage({ text: 'You already used an item today!', type: 'error' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (activating !== null) return;

    setActivating(item.user_item_id);
    setOpenTooltip(null);
    try {
      const result = await api.post('/player/me/items/use', { user_item_id: item.user_item_id }, token ?? undefined);
      if (result?.ok || result?.activity_id) {
        setMessage({ text: `${item.name} activated! 🎉`, type: 'success' });
        setLocallyUsedToday(true);
        // Item sofort aus lokalem State entfernen
        setLocalItems(prev => prev.filter(i => i.user_item_id !== item.user_item_id));
        onItemUsed?.();
      } else {
        setMessage({ text: result?.error || 'Activation failed.', type: 'error' });
      }
    } catch (err: any) {
      const errorMsg = err?.message || String(err);
      if (errorMsg.includes('heute bereits ein Workout') || errorMsg.includes('Workout')) {
        setMessage({ text: 'You already worked out today! Rest Day not possible.', type: 'error' });
      } else {
        setMessage({ text: 'Activation failed: ' + errorMsg, type: 'error' });
      }
    } finally {
      setActivating(null);
      setTimeout(() => setMessage(null), 4000);
    }
  }, [token, activating, usedToday, disabled, onItemUsed]);

  // Long-Press für Mobile
  const handleTouchStart = useCallback((item: UserItem) => {
    if (item.is_used) return;
    longPressTimer.current = setTimeout(() => {
      handleUseItem(item);
      longPressTimer.current = null;
    }, 500);
  }, [handleUseItem]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Item-Klick: Tooltip togglen (Desktop + Mobile)
  const handleItemClick = useCallback((itemId: number) => {
    setOpenTooltip(prev => prev === itemId ? null : itemId);
  }, []);

  if (!localItems || localItems.length === 0) return null;

  return (
    <div className="item-bar" ref={containerRef}>
      <div className="item-bar-header">
        <h3 className="item-bar-title">Items</h3>
        <p className="item-bar-hint">Earned after each duel result — the higher the combined score, the more items you can choose. Use 1 per day. Max 7 kept; extras are recycled.</p>
      </div>
      <div className="item-bar-items">
        {localItems.map(item => {
          const icon = ITEM_ICONS[item.icon] || faBed;
          const isUsed = !!item.is_used;
          const canUse = !isUsed && !usedToday && !disabled;

          let stateClass = 'item--available';
          if (isUsed) stateClass = 'item--used';
          else if (!canUse) stateClass = 'item--disabled';

          return (
            <div
              key={item.user_item_id}
              className={`item ${stateClass}${openTooltip === item.user_item_id ? ' item--active' : ''}${activating === item.user_item_id ? ' item--activating' : ''}`}
              onClick={() => { if (!disabled && !usedToday) handleItemClick(item.user_item_id); }}
            >
              <div className="item-icon-wrap">
                <FontAwesomeIcon icon={icon} className="item-icon" />
              </div>
              {isUsed && <div className="item-badge item-badge--used">✓</div>}
            </div>
          );
        })}
      </div>
      {/* Reveal Banner – shows revealed opponent activities (from item or ui_state) */}
      {(() => {
        // Reveal-Daten: zuerst aus Item, dann aus uiStates (neuer Flow)
        let rd: any = null;
        let bannerKey = '';
        const itemReveal = localItems.find(i => i.reveal_data && (i.reveal_data as any).type !== 'trophy' && (i.reveal_data as any).type !== 'daily_spin');
        if (itemReveal) {
          rd = itemReveal.reveal_data;
          bannerKey = `reveal-${itemReveal.user_item_id}`;
        } else if (uiStates?.reveal_data) {
          rd = typeof uiStates.reveal_data === 'string' ? JSON.parse(uiStates.reveal_data) : uiStates.reveal_data;
          bannerKey = 'reveal-ui-state';
        }
        if (!rd || localStorage.getItem(`reveal_dismissed_${bannerKey}`)) return null;
        const acts = rd.activities ?? (rd.activity_id ? [{
          activity_id: rd.activity_id!, activity_name: rd.activity_name!, sport_type: rd.sport_type!,
          total_points: rd.total_points!, base_points: rd.base_points!,
          challenge_bonus: rd.challenge_bonus!, duration_seconds: rd.duration_seconds!,
        }] : []);
        return (
          <div key={bannerKey} className="item-reveal-banner">
            <button className="item-reveal-close" onClick={() => { localStorage.setItem(`reveal_dismissed_${bannerKey}`, '1'); api.post('/player/me/items/reveal-dismiss', {}, token ?? undefined); onItemUsed?.(); }} title="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="item-reveal-header">
              <FontAwesomeIcon icon={faSearch} className="item-reveal-icon" />
              <span className="item-reveal-title">Opponent's Strongest {acts.length > 1 ? 'Activities' : 'Activity'}</span>
            </div>
            {acts.map((act: any, idx: number) => (
              <div key={act.activity_id ?? idx} className="item-reveal-body">
                <div className="item-reveal-sport">{act.sport_type}</div>
                <div className="item-reveal-name">{act.activity_name}</div>
                <div className="item-reveal-details">
                  <span className="item-reveal-points">{act.total_points} pts</span>
                  <span className="item-reveal-duration">{formatDuration(act.duration_seconds)}</span>
                  {act.challenge_bonus > 0 && (
                    <span className="item-reveal-bonus">+{act.challenge_bonus} challenge</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
{/* Tooltip – bleibt offen bis Klick außerhalb */}
      {openTooltip !== null && (() => {
        const item = localItems.find(i => i.user_item_id === openTooltip);
        if (!item) return null;
        const isUsed = !!item.is_used;
        const canUse = !isUsed && !usedToday;

        return (
          <div className="item-tooltip">
            <div className="item-tooltip-header">
              <div>
                <div className="item-tooltip-title">{item.name}</div>
                {isUsed && <div className="item-tooltip-badge">Used</div>}
                {!isUsed && usedToday && <div className="item-tooltip-badge">Not available today</div>}
              </div>
            </div>
            <div className="item-tooltip-desc">
              {item.effect_type === 'create_rest_day'
                ? `Activate this item to take a rest day today. You will receive ${item.effect_value} points.`
                : item.effect_type === 'reveal_strongest'
                  ? `Reveals your opponent's top ${item.effect_value} strongest activities this week and shows exactly how many points they scored.`
                  : item.effect_type === 'boost_strongest'
                    ? `Adds ${item.effect_value}% bonus to your strongest workout today. Not usable on RestDay.`
                    : item.effect_type === 'challenge_king_bonus'
                      ? `You've completed 3+ challenges! Claim ${item.effect_value} bonus points.`
                      : item.effect_type === 'multisport_bonus'
                        ? `Mindestens 3 different sports this week! Claim ${item.effect_value} bonus points.`
                        : item.effect_type === 'sport_junky_bonus'
                          ? `${item.effect_value} points per active day this week!`
                          : item.effect_type === 'unlock_spin'
                            ? `Reveal daily challenges for ${item.effect_value} days ahead.`
                            : item.description
              }
            </div>
            <div className="item-tooltip-divider" />
            <div className="item-tooltip-condition">
              {isUsed
                ? `Used on ${item.used_on_day ? new Date(item.used_on_day + 'T12:00:00').toLocaleDateString('en-US') : '?'}`
                : usedToday
                  ? 'You already used an item today.'
                  : item.condition_desc
              }
            </div>
            {!isUsed && canUse && (
              <button
                className="item-tooltip-activate-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUseItem(item);
                }}
                disabled={activating !== null}
              >
                {activating === item.user_item_id ? 'Activating...' : 'Use item'}
              </button>
            )}
          </div>
        );
      })()}

      {/* Feedback-Message */}
      {message && (
        <div className={`item-message item-message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
}
